<script setup lang="ts">
import { onMounted, ref, type Ref, nextTick, computed } from 'vue'
import NavLogoMenu from '@/components/business/nav/NavLogoMenu.vue'
import NavSideMenu from '@/components/business/nav/NavSideMenu.vue'
// https://github.com/Akryum/vue-virtual-scroller
import { ElDrawer, ElMessage } from 'element-plus'
import { menuCollapse } from '@/composables/settings/storage'
import { loadLifeListData } from '@/composables/life/life'
import ModalContainer from '@/pages/dialog/ModalContainer.vue'
import { currentUser, localLogout } from '@/composables/user/users'
import { useRouter, useRoute } from 'vue-router'
import { renderMessage } from '@/utils/util'
import { useI18n } from 'vue-i18n'
import NavBottom from '@/components/business/nav/NavBottom.vue'
import { retrieveNotificationCount } from '@/composables/notification/notifications'
import { useAccountStore } from '@/composables/map/map'
const { t } = useI18n()
const accountStore = useAccountStore()
const router = useRouter()
const route = useRoute()
const showBottomNav = computed(() => {
  return route.meta.showBottomNav === true
})

// Components like NavUserItem and NavTitleBar should be auto-imported by Nuxt if configured correctly.
// However, adding explicit imports if needed, or relying on Nuxt's auto-import.
// In BasePage.vue they were used as:
// <NavTitleBar ... />
// <NavUserItem ... />
// <NavLogoMenu ... />
// <NavSideMenu ... />

function onCollapseClicked(isCollapsed: boolean) {
  menuCollapse.value = isCollapsed
}

function onClose() {
  menuCollapse.value = false
}

function onSelected(key: string) {
  if (import.meta.client && window.innerWidth < 768) {
    menuCollapse.value = false
  }
  switch (key) {
    case 'logout':
      localLogout()
      router.push('/login')
      ElMessage({
        message: renderMessage(t('login.logout_sucess')),
        type: 'success'
      })
      break
    case 'details':
      router.push('/me')
      break
    case 'membership':
      router.push('/member')
      break
    default:
      break
  }
}

onMounted(() => {
  loadLifeListData()
  retriveUnreadLooply()
  accountStore.initAccount()
})

let retriveInterval: ReturnType<typeof setInterval>
function retriveUnreadLooply() {
  // every 3 minutes to call retrieveNotificationCount() one time
  retrieveNotificationCount()
  retriveInterval = setInterval(() => {
    retrieveNotificationCount()
  }, 180 * 1000) // 180s (3 minutes)
}
</script>
<template>
  <main relative bottom-0 left-0 right-0 top-0 w-full flex flex-row bg-base>
    <!-- <OfflineChecker fixed bottom-0 left-0 right-0 z-10000 /> -->
    <ClientOnly>
      <NavBottom v-if="showBottomNav" fixed bottom-0 left-0 right-0 z-9999 md:hidden />
      <!-- left -->
      <el-drawer
        relative
        v-model="menuCollapse"
        direction="ltr"
        :show-close="false"
        :with-header="false"
        :lock-scroll="false"
        @close="onClose"
        modal-class="home-drawer-overlay">
        <nav relative h-full w-full flex flex-col overflow-hidden>
          <NavLogoMenu h-14 flex-none />
          <div w-full overflow-y-auto px-4 py-4 flex-1>
            <NavSideMenu @onSelect="onSelected"></NavSideMenu>
          </div>
          <NavUserItem w-full flex-none @onAction="onSelected"></NavUserItem>
        </nav>
      </el-drawer>
    </ClientOnly>
    <!-- right -->
    <div flex="~ col" relative h-full w-full grow>
      <NavTitleBar sticky top-0 left-0 md:right-0 :menuCollapse="menuCollapse" @collapse="onCollapseClicked" />
      <div absolute top-0 left-0 right-0 md:static h-full>
        <slot />
      </div>
    </div>
    <ModalContainer />
  </main>
</template>
<style lang="scss">
body {
  width: 100% !important;
}

.home-drawer-overlay {
  z-index: 3023 !important;

  .el-drawer {
    width: 75% !important;
    max-width: 280px;
    height: 100% !important;
    overflow: hidden !important;
    background-color: var(--c-sidebar);
    scrollbar-width: none;

    .el-drawer__header {
      margin: 0px !important;
      padding: 0px !important;
      height: 0px !important;
    }

    .el-drawer__body {
      padding: 0px;
      margin: 0px !important;
      overflow: hidden !important;
      scrollbar-width: none;
      height: 100% !important;
      display: flex;
      flex-direction: column;
    }
  }
}

.home-drawer-overlay::-webkit-scrollbar {
  display: none;
}

// md
@media (min-width: 768px) {
  body {
    width: 100% !important;
  }

  .home-drawer-overlay {
    z-index: 0 !important;
    position: sticky !important;
    min-width: 17rem;
    height: 100vh !important;
    overflow: hidden !important;
    background-color: var(--c-sidebar) !important;

    .el-drawer {
      width: 100% !important;
      height: 100% !important;
      box-shadow: 0 0 var(--c-sidebar) !important;
      background-color: var(--c-sidebar);

      .el-drawer__body {
        padding: 0px;
        overflow-y: auto !important;
        scrollbar-width: none;
        height: 100% !important;
        display: flex;
        flex-direction: column;
      }

      .el-drawer__header {
        padding: 0px;
        margin-bottom: 0px;
      }
    }
  }
}
</style>
